import * as React from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const alertVariants = cva(
  'relative w-full rounded-[var(--radius-md)] border p-4 text-sm',
  {
    variants: {
      variant: {
        info:
          'border-[color:var(--color-info)] bg-[color:var(--color-info-soft)] text-[color:var(--color-fg)]',
        success:
          'border-[color:var(--color-success)] bg-[color:var(--color-success-soft)] text-[color:var(--color-fg)]',
        warn:
          'border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)] text-[color:var(--color-fg)]',
        danger:
          'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)] text-[color:var(--color-fg)]',
      },
    },
    defaultVariants: { variant: 'info' },
  }
);

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  danger: XCircle,
} as const;

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
}

export function Alert({ className, variant = 'info', title, children, ...props }: AlertProps) {
  const Icon = ICONS[variant ?? 'info'];
  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      <div className="flex items-start gap-3">
        <Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
        <div className="flex-1">
          {title ? <p className="mb-1 font-semibold">{title}</p> : null}
          <div className="text-[color:var(--color-fg-muted)]">{children}</div>
        </div>
      </div>
    </div>
  );
}
